// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { sectionTitleFields } from "@guestsupply/App/Widgets/SectionTitle/SectionTitle";
import ProductListProductCard, { ProductListProductCardStyles } from "@guestsupply/Overrides/Widgets/ProductList/ProductListProductCard";
import { fontFamilyValues } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList, getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    // Xng-Changes:
    showImage = "showImage",
    showTitle = "showTitle",
    showAttributes = "showAttributes",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    showCompare = "showCompare",
    fontFamily = "fontFamily",
    viewMoreText = "viewMoreText",
    viewMoreLink = "viewMoreLink",
    border = "border",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.showCompare]: boolean;
        [fields.fontFamily]: string;
        [fields.viewMoreText]: string;
        [fields.viewMoreLink]: string;
        [fields.border]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const products = getProductsForProductInfoList(state, ownProps.id);
    let productInfos;
    if (products?.length) {
        productInfos = products.map(product => getProductInfoFromList(state, ownProps.id, product.id))
    }
    return {
        products,
        productInfos,
        canViewPurchasedProducts: isAuthenticated,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: InjectableCss;
    itemHeaderWrapper?: InjectableCss;
    itemHeaderText?: TypographyPresentationProps;
    addToCartHeaderWrapper?: InjectableCss;
    addToCartHeaderText?: TypographyPresentationProps;
    // Xng-Changes:
    container?: InjectableCss;
    cardContainerStyles?: CardContainerStyles;
    productListProductCard?: ProductListProductCardStyles;
}

// Xng-Changes: please check PurchasedProductsExtensions.ts for styles
export const purchasedProductsStyles: PurchasedProductsStyles = {};

const styles = purchasedProductsStyles;

const PurchasedProducts = ({
    id,
    fields,
    products,
    productInfos,
    canViewPurchasedProducts,
    loadPurchasedProducts,
    updateProductInfo
}: Props) => {
    const { title, fontFamily, viewMoreText, viewMoreLink, border, purchaseType } = fields;

    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            loadPurchasedProducts({ widgetId: id, purchaseType });
        }
    }, [purchaseType]);

    if (!canViewPurchasedProducts || !products || products.length === 0 || !productInfos || productInfos.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            {createWidgetElement("SectionTitle/SectionTitle", {
                fields: { title, fontFamily, viewMoreText, viewMoreLink, border },
            })}
            <StyledWrapper {...styles.headerContainer}>
                <StyledWrapper {...styles.itemHeaderWrapper}>
                    <Typography {...styles.itemHeaderText}>{translate("Item")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.addToCartHeaderWrapper}>
                    <Typography {...styles.addToCartHeaderText}>{translate("Add to Cart")}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            {products.map((product, idx) => {
                const productContext: ProductContextModel = {
                    product,
                    productInfo: productInfos[idx]!,
                    idx,
                    onQtyOrderedChanged: qtyOrdered => {
                        updateProductInfo({ id, productId: product.id, qtyOrdered });
                    },
                    onUnitOfMeasureChanged: unitOfMeasure => {
                        updateProductInfo({ id, productId: product.id, unitOfMeasure });
                    },
                };

                return (
                    <ProductContext.Provider value={productContext} key={idx}>
                        <CardContainer extendedStyles={styles.cardContainerStyles}>
                            <ProductListProductCard {...fields} extendedStyles={styles.productListProductCard} />
                        </CardContainer>
                    </ProductContext.Provider>
                )
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts),
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            ...(sectionTitleFields as any),
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
            },
            {
                name: fields.showBrand,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showImageBadges,
                displayName: "Show Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
            },
            {
                name: fields.showTextBadges,
                displayName: "Show Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
            },
            {
                name: fields.showCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isEnabled: () => false,
            },
        ],
    },
};

export default widgetModule;
